import { Injectable } from '@angular/core'
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, NavigationEnd, Router, NavigationCancel } from '@angular/router';
import { Observable, of, combineLatest, merge } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, skip, distinctUntilKeyChanged, skipWhile, takeWhile } from 'rxjs/operators';
import { environment } from 'src/environments/environment'
import { select, Store } from '@ngrx/store'
import * as Reducers from 'src/app/store/reducers'
import { AuthService } from '@app/services/auth/jwt/auth.service';
import { PermissionService } from '@app/services/permission/permission.service';

@Injectable({
	providedIn: 'root',
})
export class AuthGuard implements CanActivate {
	authorized: boolean;

	constructor(private store: Store<any>, public router: Router, private authService: AuthService, private permissionService: PermissionService) {
		this.store.pipe(select(Reducers.getUser)).subscribe(state => {
			this.authorized = state.authorized;
		});
	}

	canActivate(
		next: ActivatedRouteSnapshot,
		state: RouterStateSnapshot,
	): Observable<boolean> | Promise<boolean> | boolean {
		if (environment.authenticated) {
			// skip guard checking on demo environment serve/build, remove it in your app
			return true;
		}

		if (!this.authorized || !this.authService.isLoggedIn) {
			this.router
				.navigate(['auth/login'], { queryParams: { returnUrl: state.url } })
				.catch(console.error);
			return false;
		}

		let permission$: Observable<boolean>;
		const permissions = next.data?.permissions as string[];
		const all_permissions = next.data?.all_permissions as string[];
		if (permissions?.length || all_permissions?.length) {
			permission$ = this.store.select(Reducers.getActiveProject).pipe(
				// So lange blockieren, bis das Aktive Projekt beim Start geladen wurde. Erst dann sind die Permissions vollständig
				filter(project => project !== undefined),
				// Observables für die einzelnen Permissions erzeugen und zu einem Observable kombinieren
				switchMap(() => {

					// mindestens eine Permission muss `true` sein
					const or_permissions$ = permissions?.length ? this.permissionService.hasAnyPermissions$(...permissions) : of(true);

					// alle Permissions müssen `true` sein
					const and_permissions$ = all_permissions?.length ? this.permissionService.hasAllPermissions$(...all_permissions) : of(true);

					// OR und AND Permissions müssen beide true liefern oder dürfen nicht gesetzt sein
					return combineLatest([or_permissions$, and_permissions$]).pipe(
						map(([or_permissions, and_permissions]) => or_permissions && and_permissions),
					);
				}),
			);
		} else {
			// Keine Permissions gesetzt, Zugang ist implizit gewährt
			permission$ = of(true);
		}

		// Zurück auf Start, wenn die Permission nicht mehr da ist
		permission$.pipe(
			// Erst aktiv werden, wenn wir einmal für die Seite berechtigt waren
			skipWhile(permission => permission !== true),
			// Danach nur reagieren, wenn die Permission nicht mehr gegeben ist
			filter(permission => permission === false),
			take(1),
			// Observable beenden, wenn wir nicht auf der Seite sind
			takeUntil(merge(
				this.router.events.pipe(
					// Navigationsabbruch. Wir sind nie auf der Seite gelandet
					takeWhile(event => !(event instanceof NavigationEnd)),
					filter(event => event instanceof NavigationCancel),
				),
				this.router.events.pipe(
					// Navigation von der Seite weg
					filter(event => event instanceof NavigationEnd),
					distinctUntilKeyChanged<NavigationEnd>('url'),
					skip(1), // Aktuelle Navigation vom AuthGuard ignorieren
				),
			)),
		).subscribe(() => {
			this.router
				.navigate(['/'])
				.catch(console.error);
		});

		return permission$;
	}
}
